import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router';
import { DEMO_MODE } from '@/lib/demoMode';
import { supabase } from '@/integrations/supabase/client';
import { IS_NATIVE as isNative, platform } from '@/platform';
import { IDLE_DEADLINE_PREF } from '@/lib/nativeSessionKeys';
import { localFirstWipe } from '@/lib/nativeSession';
import { appLock } from '@/lib/appLock';
import { AuthWipeIncompleteError } from '@/lib/nativeAuthStorage';
import { toast } from '@/hooks/use-toast';

const DEFAULT_INACTIVITY_MINUTES = 30; // production default — unchanged
const WARNING_LEAD_MS = 2 * 60 * 1000; // show the 2-minute warning before logout
// Native only (M2, brief §2.5): how often activity rewrites the persisted
// deadline. A boot after a kill may therefore end the session up to this much
// early, never late. Going to the background always writes it exactly.
const DEADLINE_WRITE_THROTTLE_MS = 15 * 1000;

// ─── TEMPORARY TESTING OVERRIDE — delete the setting/env var to revert ───────
// Extends ONLY the idle auto-logout window so Admin and Helper testers can stay
// signed in through a full testing day. Two ways to switch it on, checked in
// this order:
//   1. app_settings key 'session_inactivity_minutes_override' (RUNTIME — set it
//      in Supabase / Admin, no redeploy; revert by deleting the row). Preferred.
//   2. env var VITE_SESSION_INACTIVITY_MINUTES (build-time fallback).
// A value is the number of minutes (e.g. 720 = 12h). When neither is set —
// the default and the production state — behaviour is exactly as before:
// 30-minute idle logout for everyone.
//
// Scope: the longer window applies to Admin (a non-'user' user_roles row) and
// Helper (profiles.is_helper) ONLY; clients always keep the 30-minute default.
// Nothing else changes — roles, permissions, route guards, token refresh, and
// login/logout are untouched; this only lengthens the inactivity window.
const OVERRIDE_SETTING_KEY = 'session_inactivity_minutes_override';
const ENV_OVERRIDE = Number(import.meta.env.VITE_SESSION_INACTIVITY_MINUTES);
const ENV_OVERRIDE_MINUTES = Number.isFinite(ENV_OVERRIDE) && ENV_OVERRIDE > 0 ? ENV_OVERRIDE : null;
// ─────────────────────────────────────────────────────────────────────────────

export function useSessionTimeout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(120); // 2 minutes in seconds

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Current idle window in ms — defaults to the production 30 minutes, only
  // widened for eligible (Admin/Helper) users when an override is configured.
  // Held in refs so resetTimer stays referentially stable.
  const inactivityMsRef = useRef<number>(DEFAULT_INACTIVITY_MINUTES * 60 * 1000);
  const warningMsRef = useRef<number>(DEFAULT_INACTIVITY_MINUTES * 60 * 1000 - WARNING_LEAD_MS);
  // Effective override minutes for THIS user (null => default 30 min).
  const [overrideMinutes, setOverrideMinutes] = useState<number | null>(null);

  // Resolve the override + Admin/Helper eligibility once per signed-in user.
  useEffect(() => {
    if (DEMO_MODE || !user) {
      setOverrideMinutes(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [{ data: setting }, { data: roles }, { data: profile }] = await Promise.all([
        supabase.from('app_settings').select('setting_value').eq('setting_key', OVERRIDE_SETTING_KEY).maybeSingle(),
        supabase.from('user_roles').select('role').eq('user_id', user.id),
        supabase.from('profiles').select('is_helper').eq('id', user.id).maybeSingle(),
      ]);
      const dbMinutes = Number((setting as { setting_value?: string } | null)?.setting_value);
      const configured =
        Number.isFinite(dbMinutes) && dbMinutes > 0 ? dbMinutes : ENV_OVERRIDE_MINUTES;
      const isStaff = (roles ?? []).some((r: { role: string | null }) => !!r.role && r.role !== 'user');
      const isHelper = !!(profile as { is_helper?: boolean } | null)?.is_helper;
      if (!cancelled) setOverrideMinutes(configured && (isStaff || isHelper) ? configured : null);
    })().catch(() => {
      if (!cancelled) setOverrideMinutes(null);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Keep the active window in sync with the resolved override.
  useEffect(() => {
    const ms = (overrideMinutes ?? DEFAULT_INACTIVITY_MINUTES) * 60 * 1000;
    inactivityMsRef.current = ms;
    warningMsRef.current = Math.max(ms - WARNING_LEAD_MS, Math.floor(ms / 2));
  }, [overrideMinutes]);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    if (isNative) {
      // Every native expiry uses the local-first wipe (brief §2.5): signOut()
      // keeps the session when offline, and AuthWrapper would then send the
      // surviving user straight back in from /auth.
      try {
        await localFirstWipe();
      } catch (e) {
        if (!(e instanceof AuthWipeIncompleteError)) throw e;
        // The client is already signed out, but the phone kept the session.
        // Say so rather than report a normal sign-out (Codex on #1093).
        toast({
          title: 'Sign-out did not finish on this phone',
          description: 'Your sign-in could not be cleared from this phone. Restart Kanfy, then sign out again.',
          variant: 'destructive',
        });
      }
    } else {
      await signOut();
    }
    navigate('/auth', { replace: true });
  }, [signOut, navigate, clearAllTimers]);

  const startCountdown = useCallback((seconds = 120) => {
    setRemainingTime(seconds);
    countdownRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [handleLogout]);

  // Native: set once real activity has re-armed the window in this run.
  const activitySeenRef = useRef(false);
  // Native: the absolute deadline restored from a killed process. It holds,
  // whatever window the override later resolves to, until real activity
  // starts a new window (Codex on #1093).
  const restoredDeadlineRef = useRef<number | null>(null);
  const currentDeadline = useCallback(
    () => restoredDeadlineRef.current ?? lastActivityRef.current + inactivityMsRef.current,
    [],
  );

  // Native only: the absolute deadline, persisted so a backgrounded or killed
  // app still honours it (brief §2.5). The web keeps timers alone.
  const lastDeadlineWriteRef = useRef<number>(0);
  // Whether this session has a deadline on disk. Until it does, a kill would
  // leave the next boot nothing to enforce, so writes are not throttled.
  const deadlinePersistedRef = useRef(false);
  // The write in flight, so the first arm can check one that activity started.
  const pendingWriteRef = useRef<Promise<boolean> | null>(null);
  // True while the first arm reads the persisted deadline. Until it lands,
  // nothing but real activity may compute or write a deadline: the window
  // before the read (an override resolving, a background) would be measured
  // from mount and could overwrite a still-valid absolute deadline (Codex on
  // #1093).
  const armingRef = useRef(false);
  const writeDeadline = useCallback(async (): Promise<boolean> => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await platform.storage.prefs.set(IDLE_DEADLINE_PREF, String(currentDeadline()));
        deadlinePersistedRef.current = true;
        return true;
      } catch {
        // Retried; an older deadline on disk is earlier, so it fails closed.
      }
    }
    return false;
  }, [currentDeadline]);
  const persistDeadline = useCallback((force: boolean) => {
    if (!isNative) return;
    if (armingRef.current && !activitySeenRef.current) return;
    const now = Date.now();
    if (!force && deadlinePersistedRef.current && now - lastDeadlineWriteRef.current < DEADLINE_WRITE_THROTTLE_MS) return;
    lastDeadlineWriteRef.current = now;
    pendingWriteRef.current = writeDeadline();
  }, [writeDeadline]);

  // Arm the warning and logout timers for `remainingMs` of idle time left.
  const scheduleTimers = useCallback((remainingMs: number) => {
    clearAllTimers();
    setShowWarning(false);

    // The warning comes (inactivity - warning) before the end of the window.
    const warningLeadMs = inactivityMsRef.current - warningMsRef.current;
    const warningDelay = remainingMs - warningLeadMs;
    if (warningDelay <= 0) {
      // Already inside the warning (a resume near the end): count down from
      // what is actually left, not a fresh two minutes (Codex on #1093).
      setShowWarning(true);
      startCountdown(Math.max(1, Math.ceil(remainingMs / 1000)));
    } else {
      warningTimeoutRef.current = setTimeout(() => {
        setShowWarning(true);
        startCountdown();
      }, warningDelay);
    }

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, Math.max(remainingMs, 0));
  }, [clearAllTimers, startCountdown, handleLogout]);


  const resetTimer = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only reset if more than 1 second has passed (debounce rapid events)
    if (timeSinceLastActivity < 1000) return;

    activitySeenRef.current = true;
    restoredDeadlineRef.current = null;
    lastActivityRef.current = now;
    scheduleTimers(inactivityMsRef.current);
    persistDeadline(false);
  }, [scheduleTimers, persistDeadline]);

  // Native, first arm for a user in this process (Codex on #1093): a cold
  // start inside the window must keep the deadline the killed process
  // persisted, not start a fresh one; the boot step has already ended a
  // session whose deadline passed. With no deadline stored, a window starts
  // now and is persisted, without waiting for a first tap.
  const armFromPersistedDeadline = useCallback(async () => {
    let deadline = Number.NaN;
    let unreadable = true;
    for (let attempt = 0; attempt < 2 && unreadable; attempt++) {
      try {
        const raw = await platform.storage.prefs.get(IDLE_DEADLINE_PREF);
        if (raw !== null) deadline = Number(raw);
        unreadable = false;
      } catch {
        // Retried once below.
      }
    }
    // A deadline already on disk bounds the session even if a later write
    // fails: it is earlier, so it fails closed.
    if (!unreadable && Number.isFinite(deadline)) deadlinePersistedRef.current = true;
    // Real activity while the read was pending already armed a later window.
    // Its write must still land, or a kill leaves nothing to enforce (Codex
    // on #1093).
    if (activitySeenRef.current) {
      if (!deadlinePersistedRef.current && !(await (pendingWriteRef.current ?? writeDeadline()))) {
        if (!deadlinePersistedRef.current) void handleLogout();
      }
      return;
    }
    if (unreadable) {
      // The deadline may have passed and cannot be checked: end the session
      // rather than grant a fresh window (Codex on #1093).
      void handleLogout();
      return;
    }
    const now = Date.now();
    if (Number.isFinite(deadline)) {
      if (deadline <= now) {
        void handleLogout();
        return;
      }
      restoredDeadlineRef.current = deadline;
      deadlinePersistedRef.current = true;
      lastActivityRef.current = deadline - inactivityMsRef.current;
      scheduleTimers(deadline - now);
      return;
    }
    lastActivityRef.current = now;
    scheduleTimers(inactivityMsRef.current);
    lastDeadlineWriteRef.current = now;
    // No deadline on disk yet: a session the next boot could not bound must
    // not go on. If the write keeps failing, end it (Codex on #1093).
    if (!(await writeDeadline()) && !deadlinePersistedRef.current) void handleLogout();
  }, [handleLogout, scheduleTimers, writeDeadline]);
  const armedForUserRef = useRef<string | null>(null);

  const continueSession = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Never run in demo mode or when no user
    if (DEMO_MODE || !user) {
      clearAllTimers();
      armedForUserRef.current = null;
      return;
    }

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Native: a tap on the app lock is not activity in the app, so it must not
    // extend the idle window behind the lock (M2, brief §2.5).
    const handleActivity = () => {
      if (isNative && appLock.isLocked()) return;
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer. Native restores the persisted deadline once per
    // signed-in user. A native re-run (the override resolving) keeps the last
    // activity and applies the new window to it: re-running is not activity.
    if (!isNative) {
      resetTimer();
    } else if (armedForUserRef.current !== user.id) {
      armedForUserRef.current = user.id;
      activitySeenRef.current = false;
      restoredDeadlineRef.current = null;
      deadlinePersistedRef.current = false;
      armingRef.current = true;
      void armFromPersistedDeadline().finally(() => {
        armingRef.current = false;
      });
    } else if (armingRef.current && !activitySeenRef.current) {
      // The first arm is still reading the deadline; it applies the current
      // window (the override included) once the read lands.
    } else {
      const remaining = currentDeadline() - Date.now();
      if (remaining <= 0) {
        void handleLogout();
      } else {
        scheduleTimers(remaining);
        persistDeadline(true);
      }
    }

    // Native only: a backgrounded WebView suspends timers, so the deadline is
    // written on going to the background and checked on coming back.
    const stopStateWatch = isNative
      ? platform.app.onStateChange((active) => {
          if (!active) {
            persistDeadline(true);
            return;
          }
          const remaining = currentDeadline() - Date.now();
          if (remaining <= 0) {
            void handleLogout();
          } else {
            scheduleTimers(remaining);
          }
        })
      : null;

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      stopStateWatch?.();
      clearAllTimers();
    };
    // overrideMinutes is included so the timers re-init with the widened window
    // once the Admin/Helper override resolves.
  }, [user, resetTimer, clearAllTimers, overrideMinutes, persistDeadline, handleLogout, scheduleTimers, armFromPersistedDeadline, currentDeadline]);

  return {
    showWarning,
    remainingTime,
    continueSession,
    handleLogout,
  };
}
